'use client';

import { Button } from '@ria-client/tw-shadcn/components/button';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
} from '@ria-client/tw-shadcn/components/pagination';
import { paginationRange } from '@ria-client/tw-shadcn/lib/pagination-range';

import { ChevronLeftIcon, ChevronRightIcon } from '../icons';

export interface ResultsPaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}

export function ResultsPagination({
  page,
  totalPages,
  onPageChange,
  isLoading = false,
}: ResultsPaginationProps) {
  if (totalPages <= 1) return null;

  const pages = paginationRange({
    count: totalPages,
    page,
    siblingCount: 1,
    boundaryCount: 1,
  });

  return (
    <Pagination aria-label="Search results pagination" className="py-6">
      <PaginationContent className="gap-1.5">
        <PaginationItem>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label="Go to previous page"
            disabled={isLoading || page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            <ChevronLeftIcon className="size-6" />
          </Button>
        </PaginationItem>
        {pages.map((item, index) => (
          <PaginationItem key={`${item}-${index}`}>
            {typeof item === 'number' ? (
              <Button
                type="button"
                variant={item === page ? 'outline' : 'ghost'}
                size="icon"
                className="size-8"
                aria-label={`Go to page ${item}`}
                aria-current={item === page ? 'page' : undefined}
                disabled={isLoading}
                onClick={() => onPageChange(item)}
              >
                {item}
              </Button>
            ) : (
              <PaginationEllipsis className="size-8" />
            )}
          </PaginationItem>
        ))}
        <PaginationItem>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label="Go to next page"
            disabled={isLoading || page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            <ChevronRightIcon className="size-6" />
          </Button>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
